import { defineStore } from "pinia";
import { ref, computed } from "vue";
import semesterApi, {
    type Semester,
    type SemesterFormData,
} from "@/api/semester";

export const useSemesterStore = defineStore("semester", () => {
    // State
    const semesters = ref<Semester[]>([]);
    const currentSemester = ref<Semester | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const isActive = ref<boolean | undefined>(undefined);
    const academicYearId = ref<string | undefined>(undefined);

    // Getters
    const totalPages = computed(() => meta.value?.last_page || 0);
    const paginatedData = computed(() => semesters.value);
    const filteredSemesters = computed(() => {
        if (!search.value) return semesters.value;

        return semesters.value.filter((semester: Semester) =>
            semester.name.toLowerCase().includes(search.value.toLowerCase()),
        );
    });
    const hasSemesters = computed(() => semesters.value.length > 0);
    const isLoading = computed(() => loading.value);
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Actions
    const fetchSemesters = async (params?: {
        page?: number;
        per_page?: number;
        search?: string;
        is_active?: boolean;
        academic_year_id?: string;
    }) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await semesterApi.getSemesters({
                page: params?.page || page.value,
                per_page: params?.per_page || perPage.value,
                search: params?.search || search.value || undefined,
                is_active:
                    params?.is_active !== undefined
                        ? params?.is_active
                        : isActive.value !== undefined
                          ? isActive.value
                          : undefined,
                academic_year_id:
                    params?.academic_year_id ||
                    academicYearId.value ||
                    undefined,
            });

            semesters.value =
                (response as any).data?.items || (response as any).data || [];
            if ((response as any).data?.meta) {
                meta.value = (response as any).data.meta;
            } else if ((response as any).meta) {
                meta.value = (response as any).meta;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch semesters";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchSemester = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await semesterApi.getSemester(id);
            currentSemester.value = response.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch semester";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createSemester = async (data: SemesterFormData) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await semesterApi.createSemester(data);
            semesters.value.unshift(response.data);
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create semester";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateSemester = async (id: string, data: SemesterFormData) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await semesterApi.updateSemester(id, data);
            const index = semesters.value.findIndex((s) => s.id === id);
            if (index !== -1) {
                semesters.value[index] = response.data;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update semester";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteSemester = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await semesterApi.deleteSemester(id);
            semesters.value = semesters.value.filter(
                (semester) => semester.id !== id,
            );
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete semester";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const setActiveSemester = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await semesterApi.setActiveSemester(id);
            const index = semesters.value.findIndex((s) => s.id === id);
            if (index !== -1) {
                semesters.value[index] = response.data;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to set active semester";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const getActiveSemester = async () => {
        loading.value = true;
        error.value = null;

        try {
            const response = await semesterApi.getActiveSemester();
            currentSemester.value = response.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to get active semester";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const getSemestersByAcademicYear = async (academicYearId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const response =
                await semesterApi.getSemestersByAcademicYear(academicYearId);
            semesters.value = Array.isArray(response.data)
                ? response.data
                : [response.data];
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch semesters";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        page.value = newPage;
        fetchSemesters();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
        fetchSemesters();
    };

    const resetFilters = () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        isActive.value = undefined;
        academicYearId.value = undefined;
        fetchSemesters();
    };

    return {
        // State
        semesters,
        currentSemester,
        loading,
        error,
        page,
        perPage,
        search,
        isActive,
        academicYearId,
        meta,

        // Getters
        totalPages,
        paginatedData,
        filteredSemesters,
        hasSemesters,
        isLoading,

        // Actions
        fetchSemesters,
        fetchSemester,
        createSemester,
        updateSemester,
        deleteSemester,
        setActiveSemester,
        getActiveSemester,
        getSemestersByAcademicYear,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
